// TMOS User Approval Card Component
// Date: December 19, 2025
// Purpose: Individual approval request card with user details and actions

import React, { useState, useEffect } from 'react';
import { useLanguage } from '@/state/language/LanguageProvider';
import { UserApprovalRequest, userApprovalService } from '@/services/userApprovalService';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { 
  CheckCircleIcon, 
  XCircleIcon, 
  ArrowPathIcon,
  ExclamationTriangleIcon,
  UserIcon,
  BuildingOfficeIcon,
  EnvelopeIcon,
  ClockIcon
} from '@heroicons/react/24/outline';

interface UserApprovalCardProps {
  approval: UserApprovalRequest;
  onApprove: () => void;
  onReject: () => void;
  onMerge: () => void;
}

export const UserApprovalCard: React.FC<UserApprovalCardProps> = ({
  approval,
  onApprove,
  onReject,
  onMerge
}) => {
  const { t } = useLanguage();
  const [duplicates, setDuplicates] = useState<any>(null);

  useEffect(() => {
    if (approval.requested_company_name) {
      checkDuplicates();
    }
  }, [approval.requested_company_name]);

  const checkDuplicates = async () => {
    if (!approval.requested_company_name) return;
    
    try {
      const result = await userApprovalService.detectDuplicates(approval.requested_company_name);
      setDuplicates(result);
    } catch (error) {
      console.error('Error checking duplicates:', error);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const userName = approval.first_name || approval.last_name
    ? `${approval.first_name || ''} ${approval.last_name || ''}`.trim()
    : approval.email || 'Unknown User';

  return (
    <Card className="p-6 hover:shadow-lg transition-shadow">
      <div className="flex items-start justify-between">
        {/* Left: User Info */}
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-blue-100 rounded-full">
              <UserIcon className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">{userName}</h3>
              <div className="flex items-center gap-2 text-sm text-gray-600 mt-1">
                <EnvelopeIcon className="h-4 w-4" />
                <span>{approval.email}</span>
              </div>
            </div>
          </div>

          {/* Company Name */}
          {approval.requested_company_name && (
            <div className="mb-4">
              <div className="flex items-center gap-2 mb-2">
                <BuildingOfficeIcon className="h-4 w-4 text-gray-500" />
                <span className="text-sm font-medium text-gray-700">
                  {t('userApproval.companyName') || 'Company Name'}:
                </span>
                <span className="text-sm text-gray-900 font-semibold">
                  {approval.requested_company_name}
                </span>
              </div>

              {/* Duplicate Warning */}
              {duplicates && duplicates.has_duplicates && (
                <div className="mt-2 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <div className="flex items-start gap-2">
                    <ExclamationTriangleIcon className="h-5 w-5 text-yellow-600 mt-0.5" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-yellow-800">
                        {t('userApproval.duplicateWarning') || 'Possible duplicate company detected'}
                      </p>
                      <ul className="mt-1 text-xs text-yellow-700 space-y-1">
                        {duplicates.duplicates.slice(0, 3).map((dup: any, idx: number) => (
                          <li key={idx}>
                            • {dup.name} ({dup.similarity}% similar)
                          </li>
                        ))}
                      </ul>
                      <p className="text-xs text-yellow-600 mt-2">
                        {t('userApproval.mergeSuggestion') || 
                          'Consider merging to existing tenant instead of creating a new one.'}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Request Date */}
          <div className="flex items-center gap-2 text-sm text-gray-500 mb-4">
            <ClockIcon className="h-4 w-4" />
            <span>
              {t('userApproval.requestedAt') || 'Requested'}: {formatDate(approval.requested_at)}
            </span>
          </div>

          {/* Status Badge */}
          <div className="flex items-center gap-2">
            <Badge
              variant={
                approval.status === 'PENDING' ? 'warning' :
                approval.status === 'APPROVED' ? 'success' :
                approval.status === 'REJECTED' ? 'danger' : 'info'
              }
            >
              {approval.status}
            </Badge>
          </div>
        </div>

        {/* Right: Actions */}
        <div className="flex flex-col gap-2 ml-4">
          <Button
            onClick={onApprove}
            className="bg-green-600 hover:bg-green-700 text-white"
            size="sm"
          >
            <CheckCircleIcon className="h-4 w-4 mr-1" />
            {t('userApproval.approve') || 'Approve'}
          </Button>
          <Button
            onClick={onReject}
            variant="outline"
            className="border-red-300 text-red-600 hover:bg-red-50"
            size="sm"
          >
            <XCircleIcon className="h-4 w-4 mr-1" />
            {t('userApproval.reject') || 'Reject'}
          </Button>
          {duplicates && duplicates.has_duplicates && (
            <Button
              onClick={onMerge}
              variant="outline"
              className="border-blue-300 text-blue-600 hover:bg-blue-50"
              size="sm"
            >
              <ArrowPathIcon className="h-4 w-4 mr-1" />
              {t('userApproval.merge') || 'Merge'}
            </Button>
          )}
        </div>
      </div>
    </Card>
  );
};

